/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { GoogleGenAI, GenerateContentResponse, Type, FunctionDeclaration } from '@google/genai';
import { UsageMetadata } from '../types/types';
import { MCP_SERVER_URL } from '../types/constants';
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { ErrorHandler } from '../utils/ErrorHandler';


/**
 * Encapsulates all API calls to the Gemini model.
 */
export class GeminiService {
    private genAI: GoogleGenAI;

    constructor() {
        this.genAI = new GoogleGenAI({ apiKey: process.env.VITE_GEMINI_API_KEY! });
    }

    private toUsageMetadata(usageMetadata: GenerateContentResponse['usageMetadata']): UsageMetadata | undefined {
        if (!usageMetadata) {
            return undefined;
        }
        return {
            promptTokenCount: usageMetadata.promptTokenCount ?? 0,
            candidatesTokenCount: usageMetadata.candidatesTokenCount ?? 0,
            totalTokenCount: usageMetadata.totalTokenCount ?? 0,
        };
    }

    public async getTranscription(base64Audio: string, mimeType: string, model: string): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        const contents = [
            { text: 'Transcribe only the spoken words from this audio. Ignore and do not describe any non-speech sounds such as feedback, static, background noise, or music. The output should be a clean transcript containing only the dialogue.' },
            { inlineData: { mimeType, data: base64Audio } },
        ];
        const response: GenerateContentResponse = await this.genAI.models.generateContent({ model, contents });
        // FIX: Transform usage metadata to match the required type.
        return { text: response.text, usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }

    public async detectLanguage(text: string, model: string): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        if (!text || text.trim().length < 20) return { text: 'English', usageMetadata: undefined };
        try {
            const contents = [{ text: `What is the primary language of the following text? Respond with only the name of the language (e.g., "English", "Spanish", "Serbian"). Do not add any other words or punctuation. Text: "${text}"` }];
            const response: GenerateContentResponse = await this.genAI.models.generateContent({ model, contents });
            const language = response.text.trim().replace('.', '');
            const detectedLanguage = (language && language.length < 25 && !language.includes(' ')) ? language : 'English';
            // FIX: Transform usage metadata to match the required type.
            return { text: detectedLanguage, usageMetadata: this.toUsageMetadata(response.usageMetadata) };
        } catch (error) {
            ErrorHandler.handle('detecting language', error, null, false);
            return { text: 'English', usageMetadata: undefined };
        }
    }

    public async getPolishedNote(textToPolish: string, language: string, model: string): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        const prompt = `**Task:** You are an expert editor. Your job is to refine and polish the following text into a well-structured Markdown note.

**Input:** The following text is in **${language}**. It may be a raw transcription, a previously edited note, or translated content.
---
${textToPolish}
---

**Instructions:**
1.  **Language:** Retain the original language (${language}). Do not translate.
2.  **Clean-up:** If the text appears to be a raw transcription, eliminate filler words (e.g., "um," "uh," "like," and their equivalents in ${language}), redundancies, and false starts. If it is already edited, focus on refinement.
3.  **Formatting:** Organize the content with appropriate and clear Markdown formatting (headings, lists, bolding, etc.) to enhance readability. Improve existing formatting if necessary.
4.  **Clarity & Flow:** Correct grammatical mistakes, improve sentence structure, and enhance the overall clarity and flow of the text, preserving the original meaning.

**Output Requirements:**
- Your entire response must consist **only** of the polished note content in Markdown format.
- **Do not** include any introductory phrases, explanations, or conversational text like "Here is the polished note:".
- The response should begin directly with the first line of the note (e.g., a heading like '# Meeting Summary' or the first sentence).`;
        const contents = [{ text: prompt }];
        const response: GenerateContentResponse = await this.genAI.models.generateContent({ model, contents });
        // FIX: Transform usage metadata to match the required type.
        return { text: response.text, usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }
    
    public async getTranslation(textToTranslate: string, sourceLanguage: string | null, targetLanguage: string, model: string, inputFormat: 'text' | 'html' = 'text'): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        if (!textToTranslate.trim()) return { text: '', usageMetadata: undefined };
        let prompt = '';
        if (inputFormat === 'html') {
            prompt = `You are an expert translator specializing in technical documentation. Your task is to translate HTML content into ${targetLanguage} while perfectly preserving the structure as Markdown.

**Instructions:**
1.  Translate only the user-visible text inside the HTML tags.
2.  Convert the HTML structure (e.g., \`<h1>\`, \`<ul>\`, \`<li>\`, \`<b>\`, \`<p>\`) into the equivalent Markdown formatting (e.g., \`#\`, \`-\`, \`*\`, \`**\`, paragraphs).
3.  The final output must be **only** the translated content in pure Markdown format.
4.  Do not include any explanations, apologies, or introductory phrases like "Here is the translated Markdown:".
5.  If the original text appears to be in ${targetLanguage} already, simply convert its structure to Markdown without translating the text.

**HTML to Translate:**
---
${textToTranslate}
---`;
        } else {
            const fromLanguageClause = sourceLanguage ? `from ${sourceLanguage} ` : '';
            prompt = `Translate the following text ${fromLanguageClause}to ${targetLanguage}. 
Preserve any original markdown formatting. 
Respond **only** with the translated text, without any introductory phrases.

Text to translate:
---
${textToTranslate}
---`;
        }
        const response: GenerateContentResponse = await this.genAI.models.generateContent({ model, contents: [{ text: prompt }] });
        // FIX: Transform usage metadata to match the required type.
        return { text: response.text.trim(), usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }

    public async generateTitle(contentSnippet: string, model: string = process.env.MODEL_NAME || 'gemini-flash-latest'): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        const prompt = `Generate a concise, descriptive title (maximum 10 words) for the following note content. Respond with only the title text, without any prefixes, labels, or quotation marks.

Content:
---
${contentSnippet}
---`;
        const response: GenerateContentResponse = await this.genAI.models.generateContent({
            model: model,
            contents: [{ text: prompt }],
            config: { thinkingConfig: { thinkingBudget: 0 } }
        });
        // FIX: Transform usage metadata to match the required type.
        return { text: response.text.trim(), usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }
    
    public async rewriteSelectionForClarity(text: string, language: string, model: string): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        const prompt = `You are an expert editor. Rewrite the following text to improve its clarity, flow, and conciseness, while preserving the original meaning. 
- **Do not** add any new structural formatting like headings or lists.
- Retain the original language (${language}).
- Respond with **only** the rewritten text.

Text to rewrite:
---
${text}
---`;
        const response: GenerateContentResponse = await this.genAI.models.generateContent({
            model: model,
            contents: [{ text: prompt }],
        });
        // FIX: Transform usage metadata to match the required type.
        return { text: response.text.trim(), usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }

    public async rewriteText(text: string, language: string, model: string, instruction: string): Promise<{ text: string; usageMetadata: UsageMetadata | undefined; }> {
        const prompt = `**Task:** You are an expert editor. Your job is to rewrite the following text based on a specific instruction.

**Input Text Language:** ${language}
**Instruction:** ${instruction}

---
**Text to Rewrite:**
${text}
---

**Output Requirements:**
- Your entire response must consist **only** of the rewritten content.
- **Do not** include any introductory phrases, explanations, or conversational text.
- Retain the original language (${language}) unless the instruction specifically asks for translation.
- Preserve markdown formatting where appropriate, unless the instruction implies a format change (e.g., 'format as email').`;

        const response: GenerateContentResponse = await this.genAI.models.generateContent({
            model: model,
            contents: [{ text: prompt }],
        });
        return { text: response.text.trim(), usageMetadata: this.toUsageMetadata(response.usageMetadata) };
    }

    public async countTokens(text: string, model: string): Promise<number> {
        if (!text.trim()) return 0;
        try {
            const response = await this.genAI.models.countTokens({
                model,
                contents: text,
            });
            return response.totalTokens;
        } catch (error) {
            ErrorHandler.handle('counting tokens', error, null, false);
            throw new Error('Failed to count tokens.');
        }
    }

    // --- MCP Tool Execution ---

    public async executeAction(prompt: string, model: string): Promise<string> {
        // FIX: This comparison is invalid because the types have no overlap. The check for a placeholder URL is likely a remnant from a template and has been removed.
        if (!MCP_SERVER_URL) {
            return Promise.resolve("`ERROR: MCP_SERVER_URL is not configured in constants.tsx`");
        }

        try {
            // 1. First call to get the potential functionCall
            const response1 = await this.genAI.models.generateContent({
                model: model,
                contents: prompt,
                // FIX: The 'tools' parameter must be nested inside a 'config' object.
                config: {
                    tools: [{ functionDeclarations: this.googleWorkspaceTools }],
                },
            });

            const functionCalls = response1.functionCalls;

            if (functionCalls && functionCalls.length > 0) {
                // 2. Call MCP server for each function call
                const functionResponseParts = await Promise.all(
                    functionCalls.map(async (call) => {
                        ErrorHandler.log('MCP communication', `LLM -> MCP: ${JSON.stringify(call)}`);
                        const mcpResponse = await this.callMcpServer(call.name, call.args);
                        ErrorHandler.log('MCP communication', `MCP -> LLM: ${JSON.stringify(mcpResponse)}`);
                        return {
                            functionResponse: {
                                name: call.name,
                                response: mcpResponse,
                            },
                        };
                    })
                );

                // 3. Second call with function response(s)
                const response2 = await this.genAI.models.generateContent({
                    model,
                    contents: [
                        { text: prompt },
                        ...functionResponseParts
                    ],
                });

                return await parseMarkdown(response2.text);
            } else if (response1.text) {
                // The model responded directly without a tool call
                return await parseMarkdown(response1.text);
            }

            return "No action taken or direct response generated.";

        } catch (error) {
            ErrorHandler.handle('executing action', error, null, false);
            return "An error occurred while processing the action. Please check the console for details.";
        }
    }

    private async callMcpServer(functionName: string, args: any): Promise<any> {
        try {
            const response = await fetch(MCP_SERVER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tool: functionName,
                    params: args,
                }),
            });
            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`MCP server error: ${response.status} ${response.statusText} - ${errorBody}`);
            }
            return await response.json();
        } catch (error) {
            ErrorHandler.handle(`calling MCP server for tool ${functionName}`, error, null, false);
            return { error: `Failed to execute tool ${functionName}. Details: ${error}` };
        }
    }

    private googleWorkspaceTools: FunctionDeclaration[] = [
        {
            name: 'gmail-list-messages',
            description: 'List Gmail messages with optional search query',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    query: { type: Type.STRING, description: 'Gmail search query (e.g., "from:example@gmail.com")' },
                    maxResults: { type: Type.NUMBER, description: 'Maximum number of messages to return (min 1, max 50, default 10)' },
                    labelIds: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'Label IDs to filter by' }
                },
                required: []
            }
        },
        {
            name: 'gmail-send-message',
            description: 'Send an email via Gmail',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    to: { type: Type.STRING, description: 'Recipient email address' },
                    subject: { type: Type.STRING, description: 'Email subject' },
                    body: { type: Type.STRING, description: 'Email body content' },
                    cc: { type: Type.STRING, description: 'CC email address' },
                    bcc: { type: Type.STRING, description: 'BCC email address' }
                },
                required: ['to', 'subject', 'body']
            }
        },
        {
            name: 'drive-list-files',
            description: 'List files in Google Drive',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    query: { type: Type.STRING, description: 'Search query (e.g., "name contains \'report\'")' },
                    maxResults: { type: Type.NUMBER, description: 'Maximum number of files to return (min 1, max 50, default 10)' },
                    mimeType: { type: Type.STRING, description: 'Filter by MIME type' }
                },
                required: []
            }
        },
        {
            name: 'calendar-list-events',
            description: 'List upcoming calendar events',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    maxResults: { type: Type.NUMBER, description: 'Maximum number of events to return (min 1, max 50, default 10)' },
                    timeMin: { type: Type.STRING, description: 'Lower bound (inclusive) for event start time (RFC3339)' },
                    timeMax: { type: Type.STRING, description: 'Upper bound (exclusive) for event end time (RFC3339)' },
                    singleEvents: { type: Type.BOOLEAN, description: 'Whether to expand recurring events (default true)' }
                },
                required: []
            }
        },
        {
            name: 'calendar-create-event',
            description: 'Create a new calendar event',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    summary: { type: Type.STRING, description: 'Event title' },
                    description: { type: Type.STRING, description: 'Event description' },
                    location: { type: Type.STRING, description: 'Event location' },
                    startDateTime: { type: Type.STRING, description: 'Start date and time (RFC3339 format)' },
                    endDateTime: { type: Type.STRING, description: 'End date and time (RFC3339 format)' },
                    attendees: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'List of attendee email addresses' }
                },
                required: ['summary', 'startDateTime', 'endDateTime']
            }
        },
        {
            name: 'docs-get-document',
            description: 'Get content from a Google Docs document',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    documentId: { type: Type.STRING, description: 'Google Docs document ID' }
                },
                required: ['documentId']
            }
        },
        {
            name: 'tasks-list-tasks',
            description: 'List tasks from Google Tasks',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    tasklist: { type: Type.STRING, description: 'Task list ID (default: @default)' },
                    maxResults: { type: Type.NUMBER, description: 'Maximum number of tasks to return (min 1, max 100, default 20)' },
                    showCompleted: { type: Type.BOOLEAN, description: 'Include completed tasks (default false)' },
                    showDeleted: { type: Type.BOOLEAN, description: 'Include deleted tasks (default false)' }
                },
                required: []
            }
        },
        {
            name: 'tasks-create-task',
            description: 'Create a new task in Google Tasks',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING, description: 'Task title' },
                    notes: { type: Type.STRING, description: 'Task notes/description' },
                    due: { type: Type.STRING, description: 'Due date (RFC 3339 format)' },
                    tasklist: { type: Type.STRING, description: 'Task list ID (default: @default)' }
                },
                required: ['title']
            }
        }
    ];
}